import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { contactErrorLabel } from '@delivery/shared';

export interface ContactHit {
  id: string;
  name: string;
  phone: string;
  wrongPhones: string[];
  address: string;
  addressDetail: string | null;
  lat: number | null;
  lng: number | null;
  addressVerified: boolean;
  errorTag: string | null;
  errorNote: string | null;
  orderCount: number;
  /** 上次给这个人送收了多少(分)—— 建单时当默认价 */
  lastPrice: number | null;
  /**
   * 这个人身上**长期成立**的交代(门禁密码、走侧门、放前台)。
   *
   * ★ 建单时不塞进备注输入框,只在旁边显示一句 ——
   *   塞进去的话,换一个收件人就得把上一个人的那句从文本里抠出来,
   *   抠错就变成两句叠在一起或者把客服刚打的字吞掉。
   *   真正的合并在**服务端**建单时做(三方接口和导入也走得到)。
   */
  deliveryNote?: string | null;
  matchedByWrongPhone?: string;
}

/**
 * 收件人搜索框。
 *
 * ★ 名字叫「搜通讯录」而不是「找收件人」:同一屏上已经有一个「收件人」输入框,
 *   两个几乎同名的框摆在一起,人分不清该往哪个里填 ——
 *   读屏念出来更是两个都带「收件人」三个字。
 *   (写用例时先撞上的:`getByLabel('收件人')` 一下子匹配到两个元素。)
 *
 * ★ 一个框搜三样(姓名 / 电话 / 地址),而不是三个框。
 *   客服记得住的那一样是随机的:有时是「张姐」,有时是「尾号 8873」,
 *   有时是「上次那个 Queen Street 的」。要求她先想清楚记的是哪一类,
 *   等于把系统的分类工作推给正在打电话的人。
 *
 * ★ 每敲一个字都发一次请求的话,一个七字地址就是七次查询,
 *   而其中六次的结果在下一次按键时就作废了。停 300 毫秒再发。
 */
export function ContactPicker({
  onPick,
  customerId,
  label = '搜通讯录',
  inputId = 'contact-search',
}: {
  onPick: (c: ContactHit) => void;
  customerId?: string;
  label?: string;
  /** 同一屏上有几个搜索框时各给一个,label 才点得到自己那个框 */
  inputId?: string;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ContactHit[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const kw = q.trim();
    if (kw.length === 0) {
      setHits([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ q: kw, limit: '8' });
      if (customerId) qs.set('customerId', customerId);
      api<{ data: ContactHit[] }>(`/contacts/search?${qs.toString()}`)
        .then((r) => {
          if (cancelled) return;
          setHits(r.data);
          setOpen(true);
        })
        .catch(() => !cancelled && setHits([]))
        .finally(() => !cancelled && setBusy(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, customerId]);

  // 点到外面就收起来 —— 下拉挡着下面的表单
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <div ref={box} className="relative">
      <label className="mb-0.5 block text-xs text-slate-500" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        aria-label="搜通讯录"
        placeholder="姓名 / 电话 / 地址,任意一段"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => hits.length > 0 && setOpen(true)}
        className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm
                   dark:border-slate-600 dark:bg-slate-800"
      />
      <p className="mt-0.5 text-[11px] leading-tight text-slate-400">
        以前建过单的人在这里搜得到,选中一次填全 —— 包括坐标
      </p>

      {open && q.trim() !== '' && (
        <div
          role="listbox"
          aria-label="收件人候选"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border
                     border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800"
        >
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">
              {busy ? '搜索中…' : '没搜到 —— 直接在下面填,建完单会自动记进通讯录'}
            </div>
          ) : (
            hits.map((c) => (
              <button
                key={c.id}
                role="option"
                aria-selected={false}
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                  setQ('');
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50
                           dark:hover:bg-slate-700"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 dark:text-slate-100">{c.name}</span>
                  <span className="font-mono text-xs text-slate-500">{c.phone}</span>
                  {/*
                    ★ 有问题的联系人要在**选中之前**就看得见。
                      选完才发现「这个号打不通」的话,单已经建出去了。
                  */}
                  {c.errorTag && (
                    <span
                      className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700
                                     dark:bg-red-900/40 dark:text-red-300"
                    >
                      {contactErrorLabel(c.errorTag)}
                    </span>
                  )}
                  {c.lat == null && (
                    <span
                      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700
                                     dark:bg-amber-900/40 dark:text-amber-300"
                    >
                      没坐标
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{c.address}</div>
                {c.matchedByWrongPhone && (
                  <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                    {/* ★ 说清「现在是哪个」—— 只说「这是旧号」的话,人还要自己去对 */}
                    {c.matchedByWrongPhone} 是旧号码,现在用 {c.phone}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
